<!-- 
	This is the sign in page, it uses the dashboard layout in: 
	"./layouts/Default.vue" .
 -->

<template>
  <div class="sign-in">
    <a-row type="flex" :gutter="[24, 24]" justify="space-around" align="middle">
      <!-- Sign In Form Column -->
      <a-col :span="24" :md="12" :lg="{ span: 12, offset: 0 }" :xl="{ span: 6, offset: 2 }" class="col-form">
        <h1 class="mb-15">登录</h1>
        <h5 class="font-regular text-muted">输入邮箱密码以登录</h5>

        <!-- Sign In Form -->
        <a-form id="components-form-demo-normal-login" :form="form" class="login-form" @submit="handleSubmit"
          :hideRequiredMark="true">
          <a-form-item class="mb-10" label="邮箱" :colon="false">
            <a-input v-decorator="[
              'email',
              { rules: [{ required: true, message: '请输入您的邮箱!' }] },
            ]" placeholder="邮箱" />
          </a-form-item>
          <a-form-item class="mb-5" label="密码" :colon="false">
            <a-input v-decorator="[
              'password',
              { rules: [{ required: true, message: '请输入您的密码!' }] },
            ]" type="password" placeholder="密码" />
          </a-form-item>
          <a-form-item class="mb-10">
            <a-switch v-model="rememberMe" /> 记住密码
          </a-form-item>
          
          <!-- Cloudflare Turnstile 验证 -->
          <a-form-item class="mb-10">
            <div id="cf-turnstile" ref="turnstileContainer"></div>
            <div v-if="turnstileError" class="turnstile-error">
              {{ turnstileError }}
            </div>
          </a-form-item>
          
          <a-form-item>
            <a-button type="primary" block html-type="submit" class="login-form-button" :loading="isSubmitting">
              登录
            </a-button>
          </a-form-item>
        </a-form>
        <!-- / Sign In Form -->
        <!-- <p class="font-semibold text-muted">Don't have an account? <router-link to="/sign-in" class="font-bold text-dark">Sign Up</router-link></p> -->
      </a-col>
      <!-- / Sign In Form Column -->

      <!-- Sign In Image Column -->
      <a-col :span="24" :md="12" :lg="12" :xl="12" class="col-img">
        <img src="images/signin.gif" alt="" />
      </a-col>
      <!-- Sign In Image Column -->
    </a-row>
  </div>
</template>

<script>
import { login } from "@/api/login";
import { setToken, setPermissions, getLoginForm, setLoginForm } from "../utils/index";
import router from "../router/index";
import turnstileConfig from "@/config/turnstile.config";

export default {
  data() {
    return {
      // Binded model property for "Sign In Form" switch button for "Remember Me" .
      rememberMe: true,
      // Turnstile 相关
      turnstileToken: null,
      turnstileWidgetId: null,
      turnstileError: null,
      isSubmitting: false,
    };
  },
  beforeCreate() {
    // Creates the form and adds to it component's "form" property.
    this.form = this.$form.createForm(this, { name: "normal_login" });
  },
  mounted() {
    // Fetch user info and set form fields
    const userInfo = getLoginForm();
    if (userInfo) {
      this.$nextTick(() => {
        this.form.setFieldsValue({
          email: userInfo.username,
          password: userInfo.password,
        });
      });
    }

    // 加载 Cloudflare Turnstile
    if (turnstileConfig.enabled) {
      this.loadTurnstile();
    }
  },
  beforeDestroy() {
    // 清理 Turnstile
    if (this.turnstileWidgetId !== null && window.turnstile) {
      window.turnstile.remove(this.turnstileWidgetId);
    }
  },
  methods: {
    // 加载 Turnstile 脚本
    loadTurnstile() {
      // 检查脚本是否已加载
      if (window.turnstile) {
        this.renderTurnstile();
        return;
      }

      // 动态加载 Turnstile 脚本
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.renderTurnstile();
      };
      script.onerror = () => {
        this.turnstileError = "验证组件加载失败，请刷新页面重试";
      };
      document.head.appendChild(script);
    },

    // 渲染 Turnstile Widget
    renderTurnstile() {
      if (!window.turnstile || !this.$refs.turnstileContainer) return;

      this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileContainer, {
        sitekey: turnstileConfig.siteKey,
        theme: turnstileConfig.options.theme,
        size: turnstileConfig.options.size,
        language: turnstileConfig.options.language,
        tabindex: turnstileConfig.options.tabindex,
        callback: (token) => {
          this.turnstileToken = token;
          this.turnstileError = null;
        },
        "error-callback": () => {
          this.turnstileError = "验证失败，请重试";
          this.turnstileToken = null;
        },
        "expired-callback": () => {
          this.turnstileToken = null;
          this.turnstileError = "验证已过期，请重新验证";
        },
      });
    },

    // Handles input validation after submission.
    handleSubmit(e) {
      e.preventDefault();
      
      // 检查 Turnstile 验证
      if (turnstileConfig.enabled && !this.turnstileToken) {
        this.turnstileError = "请完成人机验证";
        return;
      }

      this.isSubmitting = true;
      this.form.validateFields((err, values) => {
        if (!err) {
          const { email, password } = values;
          const loginData = {
            username: email,
            password: password,
          };

          // 如果启用了 Turnstile，添加 token
          if (turnstileConfig.enabled && this.turnstileToken) {
            loginData.turnstileToken = this.turnstileToken;
          }

          login(loginData).then((res) => {
            if (res) {
              this.$message.success("登录成功");
              setToken(res.data.token);
              setPermissions(res.data.role);
              if (this.rememberMe) {
                setLoginForm({ username: email, password: password });
              }
              router.push("/dashboard");
            } else {
              this.$message.error("登录失败");
              // 重置 Turnstile
              if (this.turnstileWidgetId !== null && window.turnstile) {
                window.turnstile.reset(this.turnstileWidgetId);
                this.turnstileToken = null;
              }
            }
          }).catch((error) => {
            this.$message.error("登录失败，请重试");
            // 重置 Turnstile
            if (this.turnstileWidgetId !== null && window.turnstile) {
              window.turnstile.reset(this.turnstileWidgetId);
              this.turnstileToken = null;
            }
          }).finally(() => {
            this.isSubmitting = false;
          });
        } else {
          this.isSubmitting = false;
        }
      });
    },
  },
};
</script>

<style lang="scss">
body {
  background-color: #ffffff;
}

// Turnstile 样式 - 验证组件靠左对齐
#cf-turnstile {
  display: flex;
  justify-content: flex-start;
  margin: 10px 0;
}

.turnstile-error {
  color: #ff4d4f;
  font-size: 14px;
  margin-top: 8px;
  text-align: left;
}
</style>